import React from "react";

interface Threshold {
  label: string;
  value: number;
}

interface DataPoint {
  x: number;
  y: number;
}

export default function AIProgressComparison() {
  // Set dimensions and margins
  const width = 1000;
  const height = 700;
  const margin = { top: 80, right: 140, bottom: 50, left: 180 }; // Increased left margin for threshold labels
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  // Data arrays for our two curves
  // Human-only progress (linear in normal scale, reaching the max y-value by the end)
  const periods = 120;
  const months = Array.from({ length: periods }, (_, i) => `Mar ${i + 2027}`);
  const humanOnlyProgress = Array.from({ length: periods }, (_, i) => 1+ i * 1000/periods);
  
  // Real projected progress (more exponential, but squeezed in comparison)
  const realProjectedProgress = [
    1, 1000
  ];
  
  // Points for the comparison lines
  const comparisonPoint1 = 119; // Index for human-only projection (X value)
  const comparisonPoint2 = 1;  // Index for real projected progress
  
  // Threshold values and labels for the y-axis
  const thresholds = [
    { label: "Superhuman\ncoder", value: 1 },
    { label: "Superhuman AI\nResearcher", value: 25 },
    // { label: "Generally\nsuperhuman", value: 100 },
    { label: "Superintelligent\nAI Researcher", value: 250 },
    { label: "Artificial\nSuperintelligence", value: 1000 },
    { label: "Wildly\nSuperintelligent", value: 15000 },
  ];

  // Limit our chart to show only up to "Generally superhuman" (100x)
  const displayThresholds = thresholds.filter(t => t.value <= 1000);

  // Scales: x based on index (now logarithmic), y based on value (logarithmic)
  const xScale = (i: number): number => {
    // Add a small offset to ensure i is never 0 for log calculation
    const adjustedI = i + 1;
    const minLog = Math.log10(1);
    const maxLog = Math.log10(60); // Max value to display
    const logValue = Math.log10(adjustedI);
    return margin.left + ((logValue - minLog) / (maxLog - minLog)) * innerWidth;
  };
  
  // Logarithmic scale for the y-axis (unchanged)
  const yScale = (value: number): number => {
    const minLog = Math.log10(1); // Slightly below 1 to allow some margin
    const maxLog = Math.log10(1000);  // Max value to display
    const logValue = Math.log10(Math.max(value, 0.9));
    return margin.top + innerHeight - ((logValue - minLog) / (maxLog - minLog)) * innerHeight;
  };

  // Generate x-values for all data points
  const xValues = months.map((_, i) => xScale(i)-1);

  // Choose select x-axis tick indices - more points at the beginning for log scale
  const xTickIndices = [0, 2, 5, 10, 25, 50];
  
  // Generate path data for our lines
  const generateLinePath = (dataArray: number[]): string => {
    return dataArray.map((value, i) => 
      `${i === 0 ? 'M' : 'L'} ${xScale(i)} ${yScale(value)}`
    ).join(' ');
  };
  
  const humanOnlyPath = generateLinePath(humanOnlyProgress);
  const realProjectedPath = generateLinePath(realProjectedProgress);

  // Colors
  const colors = {
    humanOnly: "#50a050",    // Green for human-only progress
    realProgress: "#004000", // Darker green for real projected progress
    axis: "#444444",         // Dark gray for axis
    text: "#333333"          // Dark gray text
  };

  // Helper function to determine if a threshold is crossed in our data
  const isThresholdCrossed = (threshold: Threshold) => {
    return realProjectedProgress.some(value => value >= threshold.value);
  };

  // Find first point where threshold is crossed
  const getFirstCrossingX = (thresholdValue: number) => {
    for (let i = 0; i < realProjectedProgress.length; i++) {
      if (realProjectedProgress[i] >= thresholdValue) {
        return xScale(i);
      }
    }
    return null;
  };

  // Helper function to format the x-axis labels (show years)
  const formatXLabel = (index: number) => {
    return `Year ${index + 1}`;
  };

  return (
    <div className="w-full relative overflow-hidden mt-8 mb-8">
      <svg
        width="100%"
        preserveAspectRatio="xMidYMid meet"
        viewBox={`0 0 ${width} ${height}`}
      >
        {/* Chart Title */}
        <text
          x={margin.left}
          y={45}
          fontSize="32"
          textAnchor="left"
          fontFamily="monospace"
          className="text-[32px] font-mono md:text-[32px] sm:text-[32px]"
          fill={colors.text}
        >
          Time to Artificial Superintelligence
        </text>

        {/* Threshold lines with labels */}
        {displayThresholds.map((threshold, i) => {
          const y = yScale(threshold.value);
          if (!isThresholdCrossed(threshold)) return null;
          
          const firstCrossingX = getFirstCrossingX(threshold.value);
          if (firstCrossingX === null) return null;

          const lineCount = threshold.label.split('\n').length;
          const lineHeight = 11;
          const totalHeight = lineCount * lineHeight;
          const initialOffset = -(totalHeight / 2) + (lineHeight / 2);

          return (
            <g key={`threshold-${i}`}>
              <line 
                x1={margin.left} 
                y1={y} 
                x2={width - margin.right + 100} 
                y2={y} 
                stroke="#ddd" 
                strokeWidth="0.8" 
                strokeDasharray="5,5"
              />
              
              <text 
                x={margin.left - 10} 
                y={y + 2} 
                textAnchor="end" 
                className="text-[14px] font-mono md:text-[14px] sm:text-[14px]"
                fill={colors.text}
              >
                {threshold.label.split('\n').map((line, j) => (
                  <tspan 
                    key={j} 
                    x={margin.left - 10} 
                    dy={j === 0 ? initialOffset : lineHeight+5}
                  >
                    {line}
                  </tspan>
                ))}
              </text>
            </g>
          );
        })}

        {/* X-axis with ticks and labels */}
        {xTickIndices.map((index) => {
          if (index >= periods) return null;
          const x = xScale(index);
          return (
            <g key={`x-tick-${index}`}>
              <line 
                x1={x} 
                y1={height - margin.bottom} 
                x2={x} 
                y2={margin.top + 20} 
                stroke={colors.axis}
                strokeWidth="0.5" 
                strokeDasharray="5,5"
                opacity="0.15"
              />
              {/* X-axis labels */}
              <text
                x={x}
                y={height - margin.bottom + 20}
                textAnchor="middle"
                className="text-[12px] font-mono"
                fill={colors.text}
              >
                {index}
              </text>
            </g>
          );
        })}

        {/* Y-axis Label */}
        <text
          transform={`rotate(-90)`}
          y={10}
          x={-height / 2}
          dy="1em"
          textAnchor="middle"
          className="text-[18px] font-mono md:text-[18px] sm:text-[18px]"
          fill={colors.text}
        >
          Capability Level
        </text>

        {/* Draw the actual curve lines */}
        <path 
          d={humanOnlyPath} 
          fill="none" 
          stroke={colors.humanOnly} 
          strokeWidth="3" 
          strokeDasharray="none"
        />
        
        <path 
          d={realProjectedPath} 
          fill="none" 
          stroke={colors.realProgress} 
          strokeWidth="3" 
          strokeDasharray="none"
        />

        {/* Comparison point 1 - Dotted line down to X axis for Human-only timeline */}
        <line 
          x1={xScale(comparisonPoint1)} 
          y1={yScale(humanOnlyProgress[comparisonPoint1])} 
          x2={xScale(comparisonPoint1)} 
          y2={height - margin.bottom} 
          stroke={colors.humanOnly} 
          strokeWidth="1.5" 
          strokeDasharray="5,5"
        />
        
        {/* X label for human-only timeline */}
        <text 
          x={xScale(comparisonPoint1)} 
          y={height - margin.bottom + 32} 
          textAnchor="middle" 
          className="text-[24px] font-mono font-bold"
          fill={colors.humanOnly}
        >
          120
        </text>

        {/* Comparison point 2 - Dotted line down to X axis for real projected timeline */}
        <line 
          x1={xScale(comparisonPoint2)} 
          y1={yScale(realProjectedProgress[comparisonPoint2])} 
          x2={xScale(comparisonPoint2)} 
          y2={height - margin.bottom} 
          stroke={colors.realProgress} 
          strokeWidth="1.5" 
          strokeDasharray="5,5"
        />
        
        {/* Label for real projected timeline */}
        <text 
          x={xScale(comparisonPoint2)} 
          y={height - margin.bottom + 32} 
          textAnchor="middle" 
          className="text-[24px] font-mono font-bold"
          fill={colors.realProgress}
        >
          1.1
        </text>
        <text 
          x={margin.left + 160} 
          y={height - margin.bottom + 46} 
          textAnchor="right" 
          className="text-[22px] font-mono"
          fill={"#111111"}
        >
          Years after Superhuman Coder (log scale)
        </text>

        {/* Arrow between comparison points */}
        <defs>
          <marker
            id="arrowhead"
            markerWidth="10"
            markerHeight="7"
            refX="0"
            refY="3.5"
            orient="auto"
          >
            <polygon points="0 0, 10 3.5, 0 7" fill="#444444" />
          </marker>
        </defs>
        
        {/* Box for the arrow label */}
        {/* <rect 
          x={(xScale(comparisonPoint1) + xScale(comparisonPoint2)) / 2 - 150} 
          y={height - margin.bottom + 55} 
          width="300" 
          height="35" 
          fill="#F5F5F5" 
          stroke="#CCCCCC" 
          rx="5" 
          ry="5"
          opacity="0.9"
        /> */}
        
        {/* <line
          x1={xScale(comparisonPoint1) - 15}
          y1={height - margin.bottom - 150}
          x2={xScale(comparisonPoint2) + 25}
          y2={height - margin.bottom - 150}
          stroke="#444444"
          strokeWidth="1.5"
          markerEnd="url(#arrowhead)"
        /> */}
        
        <text
          x={(xScale(comparisonPoint1) + xScale(comparisonPoint2)) / 2.2}
          y={height - margin.bottom - 5}
          textAnchor="middle"
          className="text-[14px] font-mono font-bold"
          fill={colors.text}
        >
          AI R&D progress multiplier: 5x
        </text>
        <text
          x={(xScale(comparisonPoint1) + xScale(comparisonPoint2)) / 2.2}
          y={height - margin.bottom - 270}
          textAnchor="middle"
          className="text-[14px] font-mono font-bold"
          fill={colors.text}
        >
          AI R&D progress multiplier: 25x
        </text>
        <text
          x={(xScale(comparisonPoint1) + xScale(comparisonPoint2)) / 2.2}
          y={height - margin.bottom - 465}
          textAnchor="middle"
          className="text-[14px] font-mono font-bold"
          fill={colors.text}
        >
          AI R&D progress multiplier: 250x
        </text>
        <text
          x={(xScale(comparisonPoint1) + xScale(comparisonPoint2)) / 2.2}
          y={margin.bottom+25}
          textAnchor="middle"
          className="text-[14px] font-mono font-bold"
          fill={colors.text}
        >
          AI R&D progress multiplier: 2000x
        </text>

        {/* Legend in a box */}
        <g transform={`translate(${margin.left + 450}, ${margin.top +400})`}>
          <rect x="0" y="30" width="305" height="65" fill="#F5F5F5" rx="5" opacity="0.9" />
          {/* Human-only progress */}
          <line x1="20" y1="50" x2="50" y2="50" stroke={colors.humanOnly} strokeWidth="3" />
          <text x="60" y="55" fill={colors.text} className="text-[16px] font-mono">Human-only progress</text>
          
          {/* Real projected progress */}
          <line x1="20" y1="75" x2="50" y2="75" stroke={colors.realProgress} strokeWidth="3" />
          <text x="60" y="80" fill={colors.text} className="text-[16px] font-mono">Forecasted real progress</text>
        </g>
      </svg>
    </div>
  );
}